<template>
	<div>
		<form-remote-select v-bind="props" v-model="submitValue"
							v-model:active="isActive"
							v-model:filter="filterValue"
							:allow-create="false"
							@update:modelValue="value => emit('update:modelValue', value)"
							action="/v1/suggests/clubs"
							text-prop="display_name"
							:value-prop="props.valueProp"
							:subtitle-prop="props.subtitleProp"
							image-prop="avatar_thumbnail"
							v-model:selected="selectedClub"
							:color="props.color"
							:unselect="props.unselect"
							:data="props.data ?? {limit: 10}" image-tile>

			<template v-if="props.allowCreate" #below_options="{heightStyle}">
				<div class="border-t pt-4">
					<form-select-option :height-style="heightStyle"
										:color="props.color"
										:text="$t('action.create_name', {name: props.label ?? $t('label.club')})"
										:icon="icons.create" @toggle="createClub"/>
				</div>
			</template>
		</form-remote-select>

		<ajax-form-modal v-if="props.allowCreate" v-model="showCreateModal"
						 :form-options="{action: '/v1/clubs', data: creationData, method: 'POST'}"
						 :modal-options="{title: $t('action.create_name', {name: props.label ?? $t('label.club')})}"
						 :submit-text="$t('action.create')"
						 @success="handleCreatedClub">

			<form-input v-model="creationData.name" name="name"
						:rules="{required: true, min: 4, max: 100}"
						:placeholder="$t('placeholder.club.name')"
						:label="$t('label.name')"/>

			<form-remote-select name="presentation_type"
								:rules="{required: true}"
								:filterable="false"
								as-guest
								:color="props.color"
								:action="'/v1/club-presentation-types'"
								text-prop="title"
								value-prop="value"
								:label="$t('label.presentation_type')"
								:placeholder="$t('placeholder.select')"
								:help="$t('help.club.presentation_type')"
								v-model="creationData.presentation_type"/>

			<lazy-form-number-input v-if="props.extended" v-model="creationData.members_size"
									:rules="{required: true}"
									name="members_size"
									:label="$t('label.members_size')"/>

			<slot name="form" v-bind:data="creationData"/>

			<form-sport-type-select v-model="creationData.sport_type_id"
									name="sport_type_id"
									multiple
									persistent
									:color="props.color"
									:placeholder="$t('placeholder.select')"
									:label="$t('label.sport_types')"/>

			<template v-if="$auth.loggedIn && !props.withoutManagement">
				<template v-if="!props.requiredManagement">
					<divider/>

					<p class="subheading">{{ $t('action.manage_name_on_spoferan', {name: creationData.name ?? (props.label ?? $t('label.club'))}) }}</p>
					<p class="subheading-description">{{ $t('text.manage_name_on_spoferan', {name: creationData.name ?? (props.label ?? $t('label.club'))}) }}</p>
				</template>

				<form-checkbox name="restrictions"
							   v-model="creationData.restrictions"
							   :rules="{accepted: props.requiredManagement}"
							   :label="$t('input.club.restrictions', {club: creationData.name ?? (props.label ?? $t('label.club'))})">
					<a href="#" @click.prevent="globalStore.showGtcModal">{{ $t('action.open_app_gtc') }}</a>
				</form-checkbox>

			</template>
		</ajax-form-modal>
	</div>
</template>

<script setup lang="ts">
import {createIcon} from "../../../../config";
import {ref, watch} from "vue";
import {useGlobalStore} from "../../../../store";
import {type TextInputComponentProps} from "../../../../composables/useTextInputComponent";

const globalStore = useGlobalStore();

const props = withDefaults(defineProps<{

	// Whether custom values are allowed
	allowCreate?: boolean,

	// Whether the management option inputs shall not be shown, even if the user is logged in
	withoutManagement?: boolean,

	// Whether the management option must be accepted
	requiredManagement?: boolean,

	// Whether the extended inputs (members_size) shall be shown
	extended?: boolean,

	// The selected option object to keep in sync
	selected?: object,

	valueProp?: string,
	subtitleProp?: string,

	color?: string,
	unselect?: string,

	onlyOptions?: boolean,
	data?: object,

} & TextInputComponentProps>(), {
	valueProp: 'id',
	subtitleProp: 'name_short'
});
const emit = defineEmits(['update:modelValue', 'update:selected', 'created']);

const submitValue = ref(props.modelValue);
watch(submitValue, (newValue) => {
	emit('update:modelValue', newValue);
});
watch(() => props.modelValue, (newValue) => {
	submitValue.value = newValue;
});

const selectedClub = ref(props.selected);
watch(selectedClub, (newValue) => {
	emit('update:selected', newValue)
});
watch(() => props.selected, (newValue) => {
	selectedClub.value = newValue;
});

const isActive = ref(false);
const showCreateModal = ref(false);
const filterValue = ref(null);

// The data to create a new club
const creationData = ref({});

const icons = {
	create: createIcon
};

function createClub() {
	if (props.allowCreate) {
		creationData.value = {
			name: filterValue.value
		};
		isActive.value = false;
		showCreateModal.value = true;
	}
}

function handleCreatedClub(club) {
	submitValue.value = club.id;
	emit('created', club)
}
</script>